import { PersistentState } from '../types/GameTypes';
import { StorageService } from '../services/StorageService';
import { getTodayString, getYesterdayString } from '../utils/dateUtils';

/**
 * PersistentStateRepository - Handles persistent state (streaks, scores)
 * Manages long-term user progress and streak calculations
 */
export class PersistentStateRepository {
  private storageService: StorageService;
  
  // Storage key
  private static readonly PERSISTENT_STATE_KEY = 'rstlne_persistent_state';

  constructor(storageService: StorageService) {
    this.storageService = storageService;
  }

  /**
   * Load persistent state
   */
  loadPersistentState(): PersistentState {
    const saved = this.storageService.get<PersistentState>(PersistentStateRepository.PERSISTENT_STATE_KEY);
    if (saved) {
      return saved;
    }

    // Return default persistent state
    return {
      streak: 0,
      streakScore: 0,
      lastCompletedDate: null
    };
  }

  /**
   * Save persistent state
   */
  savePersistentState(state: PersistentState): void {
    this.storageService.set(PersistentStateRepository.PERSISTENT_STATE_KEY, state);
  }

  /**
   * Update persistent state after completing a daily challenge
   * This replicates the original StateManager.updatePersistentStateForCompletedChallenge() behavior EXACTLY
   */
  updatePersistentStateForCompletedChallenge(currentState: PersistentState, dailyScore: number): PersistentState {
    const today = getTodayString(); // Uses local timezone like original
    
    // If there's no previous completion, this is the first streak
    if (!currentState.lastCompletedDate) {
      const updatedState = {
        streak: 1,
        streakScore: dailyScore,
        lastCompletedDate: today
      };
      this.savePersistentState(updatedState);
      return updatedState;
    }
    
    // Check if the last completed date was yesterday (exact string comparison like original)
    const lastCompletedDate = currentState.lastCompletedDate;
    const yesterday = getYesterdayString(); // Uses local timezone like original
    
    // If last completed date was yesterday, increment streak
    // Otherwise reset streak to 1 (this is the critical fix)
    if (lastCompletedDate === yesterday) {
      const updatedState = {
        ...currentState,
        streak: currentState.streak + 1,
        streakScore: currentState.streakScore + dailyScore,
        lastCompletedDate: today
      };
      this.savePersistentState(updatedState);
      return updatedState;
    } else {
      // CRITICAL: Reset streak to 1, not 0! This matches original behavior
      const updatedState = {
        ...currentState,
        streak: 1,
        streakScore: dailyScore,
        lastCompletedDate: today
      };
      this.savePersistentState(updatedState);
      return updatedState;
    }
  }
}
